import "@hotwired/turbo"
import {
  type StreamElement,
  type TurboBeforeStreamRenderEvent,
  type TurboFrameMissingEvent,
  type TurboSession
} from "hotwired__turbo"
import airbrakeClient from "@/helpers/airbrake"

declare global {
  interface Window {
    Turbo: typeof Turbo
  }
}

// Hack around session apparently not having drive member
interface TurboSessionWithDrive extends TurboSession {
  drive: boolean
}

// Disable Turbo drive by default
;(Turbo.session as TurboSessionWithDrive).drive = false

// Hide frames with missing content by default
document.addEventListener("turbo:frame-missing", (e: TurboFrameMissingEvent) => {
  const target = e.target as HTMLElement
  target.hidden = true
})

// Disable Turbo prefetch across the entire app, including layouts that Turbo uses
document.addEventListener("turbo:before-prefetch", (e) => {
  e.preventDefault()
})

// Add missing `turbo:stream-render` event to mirror `turbo:frame-render`
document.addEventListener("turbo:before-stream-render", (e: TurboBeforeStreamRenderEvent) => {
  const { render } = e.detail

  e.detail.render = async (stream: StreamElement): Promise<void> => {
    await render(stream)
    ;(e.target || document).dispatchEvent(
      new CustomEvent("turbo:stream-render", {
        bubbles: true,
        cancelable: false,
        composed: true,
        detail: { stream }
      })
    )
  }
})

const renderTurboStream = async (url: string, options?: RequestInit): Promise<void> => {
  const headers = { ...(options?.headers ?? {}), Accept: "text/vnd.turbo-stream.html" }

  try {
    const response = await window.fetch(url, { ...options, headers })

    if (response.ok) {
      const html = await response.text()
      window.Turbo.session.renderStreamMessage(html)
    } else {
      void airbrakeClient.notify({
        error: `Bad stream response for url: ${url}`,
        params: {
          headers: Object.fromEntries(response.headers.entries()),
          status: response.status,
          statusText: response.statusText,
          url: response.url
        }
      })
    }
  } catch (error) {
    const isAbortError =
      (error as DOMException).name === "AbortError" || String(error).includes("AbortError")

    if (!isAbortError) {
      void airbrakeClient.notify({
        error: `Stream error for url: ${url}`,
        params: {
          error,
          url,
          visibilityState: document.visibilityState,
          online: window.navigator.onLine,
          hasFocus: document.hasFocus()
        }
      })
    }
  }
}

export { renderTurboStream }
